import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Kalkulacka.css';

// Statická definícia cien podľa typu účtovníctva (mimo komponent)
const ACCOUNTING_TYPE_PRICES = {
  'u1': { price: 1, name: 'Jednoduché – neplatca DPH' },
  'u2': { price: 1, name: 'Jednoduché – platca DPH' },
  'u3': { price: 1, name: 'Podvojné – neplatca DPH' },
  'u4': { price: 1, name: 'Podvojné – platca DPH' }
};

const COMPANY_TYPES = {
  'zivnost': 'Živnosť',
  'sro': 's. r. o.',
  'oz': 'OZ / občianske združenie',
  'ine': 'Iné'
};

const Kalkulacka = () => {
  const location = useLocation();
  const prefill = location.state?.prefill || null;

  // All hooks must be at the top level
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [loading, setLoading] = useState(false);
  // Moved up from below
  const [selectedServices, setSelectedServices] = useState([]);
  const [clientInfo, setClientInfo] = useState({
    name: prefill?.company || '',
    ico: '',
    address: '',
    date: prefill?.date || new Date().toISOString().split('T')[0]
  });
  const [companyType, setCompanyType] = useState(prefill?.companyType || '');
  const [accountingType, setAccountingType] = useState(prefill?.accountingType || '');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savePin, setSavePin] = useState('');
  const [saveNote, setSaveNote] = useState('');
  const [catalogQty, setCatalogQty] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (key) => {
    setCollapsedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Pre-fill catalogQty z prefill subtaskov (keď prichádzame z Povinností)
  useEffect(() => {
    const subtasks = prefill?.subtasks;
    if (!subtasks || subtasks.length === 0) return;
    // Pridaj podúlohy priamo ako riadky kalkulácie
    const importAccountingType = prefill?.accountingType || accountingType;
    const defaultImportPrice = importAccountingType ? (ACCOUNTING_TYPE_PRICES[importAccountingType]?.price ?? 1) : 1;
    const importedServices = subtasks
      .filter(st => st.quantity != null && Number(st.quantity) > 0)
      .map((st, i) => ({
        id: `import-${Date.now()}-${i}`,
        categoryKey: 'import',
        categoryName: 'Import z Povinností',
        name: st.text || 'Podúloha',
        unit: 'ks',
        price: 1,
        note: '',
        customPrice: true,
        quantity: Number(st.quantity),
        customPriceValue: defaultImportPrice,
      }));
    setSelectedServices(importedServices);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Aktualizácia cien customPrice položiek pri zmene typu účtovníctva
  useEffect(() => {
    if (!accountingType) return;
    const newPrice = ACCOUNTING_TYPE_PRICES[accountingType]?.price ?? 1;
    setSelectedServices(prev => prev.map(s =>
      s.customPrice ? { ...s, customPriceValue: newPrice } : s
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingType]);

  // Overenie PIN kódu cez PHP endpoint
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPinError('');
    try {
      const res = await fetch('/php/kalkulacka_pin.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput })
      });
      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (jsonErr) {
        console.error('Chyba pri parsovaní JSON odpovede:', text);
        setPinError('Neplatná odpoveď zo servera');
        setLoading(false);
        return;
      }
      console.log('API response:', data);
      if (data && data.success) {
        setIsAuthenticated(true);
      } else {
        setPinError((data && data.error) || 'Nesprávny PIN kód');
      }
    } catch (err) {
      console.error('Chyba spojenia so serverom:', err);
      setPinError('Chyba spojenia so serverom');
    }
    setLoading(false);
  };

  // Ak nie je autentifikovaný, zobraz PIN formulár
  if (!isAuthenticated) {
    return (
      <div className="kalkulacka-container">
        <div className="pin-login-box" style={{
          maxWidth: '400px',
          margin: '100px auto',
          padding: '40px',
          background: 'white',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333' }}>🔒 Prístup ku kalkulačke</h2>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Zadajte PIN kód pre prístup ku kalkulačke
          </p>
          <form onSubmit={handlePinSubmit}>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Zadajte PIN kód"
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '15px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
              autoFocus
              disabled={loading}
            />
            {pinError && (
              <div style={{ color: 'red', marginBottom: '15px' }}>{pinError}</div>
            )}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: loading ? 'wait' : 'pointer',
                fontWeight: 'bold',
                opacity: loading ? 0.7 : 1
              }}
              disabled={loading}
            >
              {loading ? 'Overujem...' : 'Prihlásiť sa'}
            </button>
          </form>
          <Link 
            to="/" 
            style={{
              display: 'block',
              textAlign: 'center',
              marginTop: '20px',
              color: '#667eea',
              textDecoration: 'none'
            }}
          >
            ← Späť na hlavnú stránku
          </Link>
        </div>
      </div>
    );
  }

  // Definícia cenníka služieb
  const serviceCategories = {
    pausalne: {
      name: "Paušálne poplatky - minimálna cena",
      services: [
        { id: 'p1', name: 'Jednoduché účtovníctvo – neplatca DPH', unit: 'mesačne', price: 90, note: '' },
        { id: 'p2', name: 'Jednoduché účtovníctvo – platca DPH', unit: 'mesačne', price: 105, note: '' },
        { id: 'p3', name: 'Podvojné účtovníctvo – neplatca DPH', unit: 'mesačne', price: 90, note: '' },
        { id: 'p4', name: 'Podvojné účtovníctvo – platca DPH', unit: 'mesačne', price: 120, note: '' },
      ]
    },
    uctonictvo: {
      name: "Položkové účtovníctvo",
      services: [
        { id: 'u5', name: 'VFA (vyšlé faktúry)', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u6', name: 'DFA (došlé faktúry)', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u7', name: 'Pohľadávky', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u8', name: 'Záväzky', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u9', name: 'Banka – príjmy aj výdaje', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u10', name: 'Pokladňa – príjmy aj výdaje', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u13', name: 'ID (interné doklady)', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u14', name: 'Iné', unit: 'za položku', price: 1, note: '', customPrice: true },
        { id: 'u15', name: 'Podanie DPH a KV k DPH', unit: 'mesačne', price: 1, note: '', customPrice: true },
        { id: 'u16', name: 'Podanie DP k DPH', unit: 'jednorazovo', price: 25, note: '' },
        { id: 'u17', name: 'Vystavovanie faktúr', unit: 'za kus', price: 5, note: '' },
        { id: 'u18', name: 'Príplatok za neúplne dodané podklady - nad 5 ks', unit: 'jednorazovo', price: 50, note: '' },
        { id: 'u19', name: 'Príplatok za zrýchlené spracovanie', unit: 'jednorazovo', price: 50, note: '' },
        { id: 'u20', name: 'Príplatok za chýbajúci dokument do 5 dokumentov', unit: 'jednorazovo', price: 5, note: '' },
        { id: 'u21', name: 'Komplexné spracovanie majetku na úver/splátky', unit: 'jednorazovo', price: 150, note: '' },
        { id: 'u22', name: 'Ľahké zaradenie majetku', unit: 'jednorazovo', price: 50, note: '' }
      ]
    },
    mzdy: {
      name: "Mzdy a personalistika",
      services: [
        { id: 'm1', name: 'Spracovanie mzdy (1 zamestnanec)', unit: 'mesačne', price: 10, note: 'Vrátane výkazov a prehľadov' },
        { id: 'm2', name: 'Registrácia zamestnávateľa (SP, ZP, DÚ)', unit: 'jednorazovo', price: 15, note: '' },
        { id: 'm3', name: 'Registrácia zamestnanca (SP, ZP)', unit: 'jednorazovo', price: 15, note: '' },
        { id: 'm4', name: 'Odhlásenie zamestnanca', unit: 'jednorazovo', price: 15, note: '' },
        { id: 'm5', name: 'Pracovná zmluva / dodatok', unit: 'jednorazovo', price: 15, note: '' },
        { id: 'm7', name: 'Ročné zúčtovanie dane', unit: 'ročne', price: 20, note: '' },
        { id: 'm8', name: 'Ročné hlásenie dane', unit: 'ročne', price: 20, note: '' }
      ]
    },
    controlling: {
      name: "Controlling",
      services: [
        { id: 'c1', name: 'Predbežný HV', unit: 'mesačne', price: 70, note: '' },
        { id: 'c2', name: 'Analytický výstup', unit: 'jednorazovo', price: 50, note: '' },
        { id: 'c3', name: 'Cashflow forecast', unit: 'jednorazovo', price: 100, note: '' },
        { id: 'c4', name: 'Porovnanie plán vs. skutočnosť', unit: 'mesačne', price: 50, note: 'Odchýlky, komentár' },
        { id: 'c5', name: 'Mesačný komentovaný report', unit: 'mesačne', price: 100, note: 'Tržby, náklady, marža, odporúčania' },
        { id: 'c6', name: 'Ad-hoc simulácie a prepočty', unit: 'za hodinu', price: 50, note: 'Na vyžiadanie' },
        { id: 'c7', name: 'Optimalizácia nákladov', unit: 'jednorazovo', price: 150, note: 'Analytická práca', priceRange: true },
        { id: 'c8', name: 'Rozpočtovanie / budget', unit: 'jednorazovo', price: 250, note: 'Strategický nástroj', priceRange: true },
        { id: 'c9', name: 'Finančný plán firmy', unit: 'jednorazovo', price: 450, note: 'Strategický nástroj (300–600 €)', priceRange: true }
      ]
    },
    dane: {
      name: "Daňové priznania a účtovná závierka",
      services: [
        { id: 'd1', name: 'Účtovná závierka PO', unit: 'ročne', price: 180, note: '' },
        { id: 'd2', name: 'Daňové priznanie PO', unit: 'ročne', price: 80, note: '' },
        { id: 'd3', name: 'Daňové priznanie FO typ B (paušál)', unit: 'ročne', price: 225, note: '' },
        { id: 'd4', name: 'Daňové priznanie FO typ B s účtovnou závierkou', unit: 'ročne', price: 225, note: '' },
        { id: 'd5', name: 'Daňové priznanie FO typ A', unit: 'ročne', price: 35, note: '' },
        { id: 'd6', name: 'Daň z motorových vozidiel (1MV)', unit: 'ročne', price: 35, note: '' },
        { id: 'd8', name: 'Dobrovoľná registrácia na DPH - základ', unit: 'jednorazovo', price: 200, note: '' },
        { id: 'd9', name: 'Dobrovoľná registrácia na DPH - rozšírená', unit: 'jednorazovo', price: 350, note: 'Zastupovanie, príprava podkladov vo väčšom rozsahu' }
      ]
    },
    doplnkove: {
      name: "Doplnkové služby",
      services: [
        { id: 'do2', name: 'Kniha jázd', unit: 'mesačne', price: 40, note: '' },
        { id: 'do3', name: 'Cestovný príkaz (tuzemský / zahraničný)', unit: 'jednorazovo', price: 25, note: '' },
        { id: 'do4', name: 'Žiadosť o vrátenie DPH zo zahraničia', unit: 'za doklad', price: 0.5, note: 'Podanie +15 €', additionalFee: 15 },
        { id: 'do5', name: 'Excel / reporty pre klienta', unit: 'za hodinu', price: 20, note: '' },
        { id: 'do6', name: 'Smernice, interné dokumenty', unit: 'za hodinu', price: 50, note: '', priceRange: true },
        { id: 'do7', name: 'Admin hod sadzba 1 (administratívne hodiny)', unit: 'za hodinu', price: 35, note: '' },
        { id: 'do8', name: 'Admin hod sadzba 2 (náročnejšie úlohy)', unit: 'za hodinu', price: 50, note: '' }
      ]
    },
    zakladanie: {
      name: "Zakladanie a zmeny spoločností / živnosti / OZ",
      services: [
        { id: 'z1', name: 'Založenie s. r. o.', unit: 'jednorazovo', price: 300, note: '' },
        { id: 'z2', name: 'Zmena v s. r. o. (bez prevodu OP)', unit: 'jednorazovo', price: 250, note: '' },
        { id: 'z3', name: 'Zmena v s. r. o. (s prevodom OP)', unit: 'jednorazovo', price: 300, note: '' },
        { id: 'z4', name: 'Založenie živnosti', unit: 'jednorazovo', price: 15, note: '' },
        { id: 'z5', name: 'Zmena v živnosti', unit: 'jednorazovo', price: 10, note: '' },
        { id: 'z6', name: 'Zrušenie živnosti', unit: 'jednorazovo', price: 10, note: '' },
        { id: 'z7', name: 'Založenie občianskeho združenia', unit: 'jednorazovo', price: 150, note: '' }
      ]
    },
    priplatky: {
      name: "Príplatky",
      services: [
        { id: 'pr6', name: 'Predzávierkové operácie a kontrola', unit: 'jednorazovo', price: 100, note: '' }
      ]
    }
  };



  // Nastavenie ceny pre nové služby podľa zvoleného typu účtovníctva
  const getDefaultPriceForService = (service) => {
    if (service.customPrice && accountingType) {
      return ACCOUNTING_TYPE_PRICES[accountingType]?.price || service.price || 0;
    }
    return service.price || 0;
  };

  // Zobrazená cena v katalógu pre customPrice položky
  const getCatalogDisplayPrice = (service) => {
    if (!service.customPrice) return `${service.price} €`;
    if (accountingType) return `${ACCOUNTING_TYPE_PRICES[accountingType]?.price ?? service.price} €`;
    return `${service.price} €`;
  };

  // Pridanie služby
  const addService = (categoryKey, service, qty) => {
    const defaultPrice = getDefaultPriceForService(service);
    const parsedQty = Math.max(0, parseFloat(qty) || 0);
    const newService = {
      id: `${service.id}-${Date.now()}`,
      categoryKey,
      categoryName: serviceCategories[categoryKey].name,
      ...service,
      quantity: parsedQty,
      customPriceValue: defaultPrice
    };
    setSelectedServices(prev => [...prev, newService]);
    setCatalogQty(prev => ({ ...prev, [service.id]: 0 }));
  };

  const handleCatalogQtyChange = (categoryKey, service, rawValue) => {
    setCatalogQty(prev => ({ ...prev, [service.id]: rawValue }));
    const parsedQty = Math.max(0, parseFloat(rawValue) || 0);
    const stableId = `catalog-${service.id}`;
    setSelectedServices(prev => {
      // Hľadaj podľa stableId AJ podľa service.id (pre staré záznamy bez prefixu)
      const exists = prev.find(s => s.id === stableId || s.id === service.id);
      if (parsedQty === 0) {
        return prev.filter(s => s.id !== stableId && s.id !== service.id);
      }
      if (exists) {
        return prev.map(s => (s.id === stableId || s.id === service.id) ? { ...s, id: stableId, quantity: parsedQty } : s);
      }
      const defaultPrice = getDefaultPriceForService(service);
      return [...prev, {
        categoryKey,
        categoryName: serviceCategories[categoryKey].name,
        ...service,
        id: stableId,        // id musí byť AŽ PO spreade, aby service.id neprepísalo stableId
        quantity: parsedQty,
        customPriceValue: defaultPrice,
      }];
    });
  };

  // Odstránenie služby
  const removeService = (id) => {
    setSelectedServices(selectedServices.filter(s => s.id !== id));
  };

  // Aktualizácia množstva
  const updateQuantity = (id, quantity) => {
    setSelectedServices(selectedServices.map(s => 
      s.id === id ? { ...s, quantity: Math.max(0, parseFloat(quantity) || 0) } : s
    ));
  };

  // Aktualizácia vlastnej ceny
  const updateCustomPrice = (id, price) => {
    setSelectedServices(selectedServices.map(s => 
      s.id === id ? { ...s, customPriceValue: parseFloat(price) || 0 } : s
    ));
  };

  // Výpočet celkovej sumy
  const calculateTotal = () => {
    let totalSum = 0;
    let accountingServicesSum = 0;
    let accountingType = null;
    
    // Najprv vypočítame súčty
    selectedServices.forEach(service => {
      const price = service.customPrice ? service.customPriceValue : service.price;
      let itemTotal = price * service.quantity;
      
      // Pridať príplatok ak existuje
      if (service.additionalFee) {
        itemTotal += service.additionalFee * service.quantity;
      }
      
      // Ak je to položkové účtovníctvo (kategória uctonictvo), pridaj do špeciálneho súčtu
      if (service.categoryKey === 'uctonictvo') {
        accountingServicesSum += itemTotal;
        // Zisti typ účtovníctva z prvej položky
        if (!accountingType) {
          const baseId = service.id.split('-')[0];
          accountingType = baseId;
        }
      } else {
        totalSum += itemTotal;
      }
    });
    
    // Aplikuj minimálny poplatok na celý súčet položkového účtovníctva
    if (accountingServicesSum > 0 && accountingType) {
      const minimumFeeMapping = {
        'u1': 60,   // Jednoduché – neplatca DPH
        'u2': 90,   // Jednoduché – platca DPH
        'u3': 90,   // Podvojné – neplatca DPH
        'u4': 130   // Podvojné – platca DPH
      };
      
      const minimumFee = minimumFeeMapping[accountingType];
      if (minimumFee && accountingServicesSum < minimumFee) {
        accountingServicesSum = minimumFee;
      }
    }
    
    return totalSum + accountingServicesSum;
  };

  // Aplikovanie percentuálneho príplatku
  const applyPercentageSurcharge = () => {
    const percentageServices = selectedServices.filter(s => s.isPercentage);
    if (percentageServices.length === 0) return 0;
    
    const baseTotal = selectedServices
      .filter(s => !s.isPercentage)
      .reduce((total, service) => {
        const price = service.customPrice ? service.customPriceValue : service.price;
        return total + (price * service.quantity);
      }, 0);
    
    const percentageTotal = percentageServices.reduce((total, service) => {
      return total + (baseTotal * (service.customPriceValue / 100));
    }, 0);
    
    return percentageTotal;
  };

  const totalAmount = calculateTotal() + applyPercentageSurcharge();

  const getPrintDisplayPrice = (service) => {
    if (service.customPrice && accountingType) {
      return (ACCOUNTING_TYPE_PRICES[accountingType]?.price ?? service.price) || 0;
    }
    return service.price || 0;
  };

  const ACCOUNTING_TYPE_PRINT_LABELS = {
    u1: 'PU neplatca DPH',
    u2: 'PU platca DPH',
    u3: 'Podvojné – neplatca DPH',
    u4: 'Podvojné – platca DPH'
  };

  const PAUSAL_MAPPING_BY_ACCOUNTING = {
    u1: 'p1',
    u2: 'p2',
    u3: 'p3',
    u4: 'p4'
  };

  const handlePrintPriceList = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Otváranie nového okna je zablokované. Skúste to prosím znova.');
      return;
    }

    const selectedCompanyType = companyType ? COMPANY_TYPES[companyType] : 'Neuvedené';
    const selectedAccountingType = accountingType ? ACCOUNTING_TYPE_PRINT_LABELS[accountingType] : 'Neuvedené';
    const selectedPausalId = accountingType ? PAUSAL_MAPPING_BY_ACCOUNTING[accountingType] : null;

    const rows = Object.entries(serviceCategories).flatMap(([categoryKey, category]) => {
      const servicesToPrint = categoryKey === 'pausalne' && selectedPausalId
        ? category.services.filter(service => service.id === selectedPausalId)
        : category.services;

      return servicesToPrint.map(service => {
        const price = getPrintDisplayPrice(service);
        return `
          <tr>
            <td>${category.name}</td>
            <td>${service.name}</td>
            <td>${service.unit}</td>
            <td style="text-align:right">${price.toFixed(2)} €</td>
            <td>${service.note || ''}</td>
          </tr>`;
      });
    }).join('');

    const html = `<!DOCTYPE html>
      <html lang="sk">
        <head>
          <meta charset="utf-8" />
          <title>Cenník služieb</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; margin: 24px; }
            h1 { font-size: 22px; margin-bottom: 8px; }
            h2 { font-size: 16px; margin: 8px 0; }
            .info { margin-bottom: 18px; }
            .info p { margin: 3px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 14px; }
            th, td { border: 1px solid #666; padding: 8px 10px; font-size: 12px; }
            th { background: #f4f4f4; }
            td { vertical-align: top; }
          </style>
        </head>
        <body>
          <h1>Cenník služieb</h1>
          <div class="info">
            <p><strong>Typ firmy:</strong> ${selectedCompanyType}</p>
            <p><strong>Typ účtovníctva:</strong> ${selectedAccountingType}</p>
            <p><strong>Dátum:</strong> ${new Date().toLocaleDateString('sk-SK')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Kategória</th>
                <th>Služba</th>
                <th>Jednotka</th>
                <th>Cena/jedn.</th>
                <th>Poznámka</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Export do PDF/Tlač
  const handlePrint = () => {
    window.print();
  };

  // Export do CSV
  const exportToCSV = () => {
    let csv = 'Kategória;Služba;Jednotka;Cena/jedn.;Množstvo;Spolu;Poznámka\n';
    
    selectedServices.forEach(service => {
      const price = service.customPrice ? service.customPriceValue : service.price;
      let itemTotal = price * service.quantity;
      
      // Pridať príplatok ak existuje
      if (service.additionalFee) {
        itemTotal += service.additionalFee * service.quantity;
      }
      
      csv += `"${service.categoryName}";"${service.name}";"${service.unit}";${price};${service.quantity};${itemTotal.toFixed(2)};"${service.note}"\n`;
    });
    
    csv += `\n;;;;Celková suma:;${totalAmount.toFixed(2)};\n`;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `kalkulacia_${clientInfo.date}.csv`;
    link.click();
  };

  // Uloženie do evidencie práce
  const handleSaveToEvidence = async () => {
    if (!savePin.trim()) {
      alert('Zadajte PIN');
      return;
    }
    if (!clientInfo.name.trim()) {
      alert('Zadajte názov firmy v informáciách o klientovi');
      return;
    }
    if (selectedServices.length === 0) {
      alert('Niet čo uložiť - vyberte služby');
      return;
    }

    try {
      const API_URL = '/php/work_tracking_api.php';
      let savedCount = 0;
      let errors = [];

      for (const service of selectedServices) {
        const price = service.customPrice ? service.customPriceValue : service.price;
        let itemTotal = price * service.quantity;
        if (service.additionalFee) {
          itemTotal += service.additionalFee * service.quantity;
        }

        const entry = {
          id: `kal_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
          companyName: clientInfo.name,
          date: clientInfo.date,
          description: service.name,
          service: service.categoryName,
          quantity: service.quantity,
          unitPrice: price,
          actualInvoicePrice: 0,
          total: itemTotal,
          note: saveNote,
          invoiced: false,
          createdAt: new Date().toISOString()
        };

        const response = await fetch(`${API_URL}?action=add_entry&pin=${encodeURIComponent(savePin)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entry })
        });
        const data = await response.json();
        if (data.success) {
          savedCount++;
        } else {
          errors.push(service.name);
        }
      }

      if (errors.length === 0) {
        alert(`Úspešne uložené ${savedCount} záznamov do evidencie práce!`);
        setShowSaveDialog(false);
        setSavePin('');
        setSaveNote('');
      } else {
        alert(`Uložené: ${savedCount}, chyba pri: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Chyba:', error);
      alert('Chyba pri ukladaní do evidencie práce');
    }
  };

  return (
    <div className="kalkulacka-container">
      <div className="no-print">
        <div className="dashboard-header">
          <h1>Kalkulačka služieb</h1>
          <Link to="/evidencia-prace" className="btn-evidence">📋 evidencia práce</Link>
        </div>
        
        {/* Výber typu účtovníctva */}
        <div className="accounting-type-selector">
          <div className="card-title">Typ účtovníctva</div>
          <div className="accounting-type-selector-inner">
            <div className="segment-group">
              {Object.entries(ACCOUNTING_TYPE_PRICES).map(([key, info]) => (
                <button
                  key={key}
                  onClick={() => setAccountingType(key)}
                  className={`btn-seg${accountingType === key ? ' active' : ''}`}
                >
                  {info.name} ({info.price} €)
                </button>
              ))}
              {accountingType && (
                <button onClick={() => setAccountingType('')} className="btn-seg-reset">
                  ✕ Zrušiť
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Výber typu firmy */}
        <div className="accounting-type-selector">
          <div className="card-title">Typ firmy</div>
          <div className="accounting-type-selector-inner">
            <div className="segment-group">
              {Object.entries(COMPANY_TYPES).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setCompanyType(key)}
                  className={`btn-seg${companyType === key ? ' active' : ''}`}
                >
                  {label}
                </button>
              ))}
              {companyType && (
                <button onClick={() => setCompanyType('')} className="btn-seg-reset">
                  ✕ Zrušiť
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Informácie o klientovi */}
        <div className="client-info">
          <div className="card-title">Informácie o klientovi</div>
          <div className="client-info-body">
            <div className="form-row">
              <label>
                Názov / Meno:
                <input
                  type="text"
                  value={clientInfo.name}
                  onChange={(e) => setClientInfo({...clientInfo, name: e.target.value})}
                  placeholder="Názov klienta"
                />
              </label>
              <label>
                Dátum:
                <input
                  type="date"
                  value={clientInfo.date}
                  onChange={(e) => setClientInfo({...clientInfo, date: e.target.value})}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Katalóg služieb */}
        <div className="services-catalog">
          <div className="card-title">Katalóg služieb</div>
          <div className="catalog-body">
            {Object.entries(serviceCategories).map(([key, category]) => {
              const isCollapsed = collapsedCategories[key] === undefined ? true : !!collapsedCategories[key];
              return (
                <div key={key} className="category-section">
                  <div
                    className="category-header"
                    onClick={() => toggleCategory(key)}
                    style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>{category.name}</span>
                    <span style={{ fontSize: 12, opacity: 0.7 }}>{isCollapsed ? '▶' : '▼'}</span>
                  </div>
                  {!isCollapsed && (
                    <table className="services-list-table">
                      <tbody>
                        {category.services.map(service => (
                          <tr
                            key={service.id}
                            className="service-row"
                          >
                            <td className="svc-name">{service.name}</td>
                            <td className="svc-note">{service.note || ''}</td>
                            <td className="svc-price">{getCatalogDisplayPrice(service)}</td>
                            <td className="svc-unit">{service.unit}</td>
                            <td className="svc-add" onClick={e => e.stopPropagation()}>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={catalogQty[service.id] ?? 0}
                                onChange={e => handleCatalogQtyChange(key, service, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); } }}
                                className="catalog-qty-input"
                                title="Počet kusov — automaticky sa pridá do kalkulácie"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Vybrané služby - viditeľné aj pri tlači */}
      <div className="selected-services">
        <div className="calculation-header">
          <h2>Kalkulácia služieb</h2>
          <div className="no-print action-buttons">
            <button onClick={handlePrint} className="btn-primary">
              🖨️ Tlačiť / PDF
            </button>
            <button onClick={handlePrintPriceList} className="btn-secondary">
              📋 Vytlačiť cenník
            </button>
            <button onClick={exportToCSV} className="btn-secondary">
              📊 Export CSV
            </button>
            <button onClick={() => setShowSaveDialog(true)} className="btn-evidence">
              💾 Uložiť do evidencie práce
            </button>
          </div>
        </div>

        {/* Informácie o klientovi pre tlač */}
        <div className="print-only client-info-print">
          {clientInfo.name && <p><strong>Klient:</strong> {clientInfo.name}</p>}
          {companyType && <p><strong>Typ firmy:</strong> {COMPANY_TYPES[companyType]}</p>}
          {accountingType && <p><strong>Typ účtovníctva:</strong> {ACCOUNTING_TYPE_PRICES[accountingType]?.name}</p>}
          {clientInfo.ico && <p><strong>IČO:</strong> {clientInfo.ico}</p>}
          {clientInfo.address && <p><strong>Adresa:</strong> {clientInfo.address}</p>}
          <p><strong>Dátum:</strong> {new Date(clientInfo.date).toLocaleDateString('sk-SK')}</p>
        </div>

        {selectedServices.length === 0 ? (
          <div className="empty-state">
            <p>Žiadne služby neboli pridané. Vyberte služby z katalógu vyššie.</p>
          </div>
        ) : (
          <>
            <table className="services-table">
              <thead>
                <tr>
                  <th>Kategória</th>
                  <th>Služba</th>
                  <th>Jednotka</th>
                  <th>Cena/jedn.</th>
                  <th>Počet</th>
                  <th>Spolu</th>
                  <th className="no-print">Akcie</th>
                </tr>
              </thead>
              <tbody>
                {selectedServices.map(service => {
                  const price = service.customPrice ? service.customPriceValue : service.price;
                  let itemTotal = price * service.quantity;
                  
                  // Pridať príplatok ak existuje
                  if (service.additionalFee) {
                    itemTotal += service.additionalFee * service.quantity;
                  }
                  
                  return (
                    <tr key={service.id}>
                      <td>{service.categoryName}</td>
                      <td>
                        {service.name}
                        {service.note && <div className="note-small">{service.note}</div>}
                      </td>
                      <td>{service.unit}</td>
                      <td>
                        {service.customPrice || service.priceRange ? (
                          <input 
                            type="number"
                            step="0.01"
                            min="0"
                            value={service.customPriceValue}
                            onChange={(e) => updateCustomPrice(service.id, e.target.value)}
                            className="price-input"
                          />
                        ) : (
                          `${price.toFixed(2)} €`
                        )}
                        {service.additionalFee && <div className="note-small">+{service.additionalFee} € príplatok</div>}
                      </td>
                      <td>
                        <input 
                          type="number"
                          step="1"
                          min="0"
                          value={service.quantity}
                          onChange={(e) => updateQuantity(service.id, e.target.value)}
                          className="quantity-input"
                        />
                      </td>
                      <td className="amount">{itemTotal.toFixed(2)} €</td>
                      <td className="no-print">
                        <button 
                          onClick={() => removeService(service.id)}
                          className="btn-remove"
                          title="Odstrániť"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="5"><strong>CELKOVÁ SUMA:</strong></td>
                  <td className="amount"><strong>{totalAmount.toFixed(2)} €</strong></td>
                  <td className="no-print"></td>
                </tr>
              </tfoot>
            </table>

            <div className="no-print clear-all">
              <button 
                onClick={() => setSelectedServices([])}
                className="btn-danger"
              >
                🗑️ Vymazať všetko
              </button>
            </div>
          </>
        )}
      </div>

      {/* Dialóg pre uloženie do evidencie */}
      {showSaveDialog && (
        <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Uložiť do evidencie práce</h2>
            <p style={{marginBottom: '12px', color: '#64748b', fontSize: '12px'}}>
              Vybrané služby sa uložia do evidencie práce pod váš PIN
            </p>
            <div className="modal-field">
              <label>PIN *</label>
              <input
                type="password"
                value={savePin}
                onChange={(e) => setSavePin(e.target.value)}
                placeholder="Váš PIN pre evidenciu"
              />
            </div>
            <div className="modal-field">
              <label>Poznámka (nepovinné)</label>
              <input
                type="text"
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                placeholder="Napr. práca za december 2025"
              />
            </div>
            <div className="modal-summary">
              <strong>Firma:</strong> {clientInfo.name || '(nezadané)'}<br />
              <strong>Dátum:</strong> {clientInfo.date}<br />
              <strong>Počet služieb:</strong> {selectedServices.length}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowSaveDialog(false)} className="btn-modal-cancel">Zrušiť</button>
              <button onClick={handleSaveToEvidence} className="btn-modal-save">💾 Uložiť</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kalkulacka;
